import { useState, useEffect } from 'react';
import { Location, LocationConfig } from '../types/flow.types';
import dataProvider from '../../services/api';

interface ErrorHandlerProps {
  showSuccessMessage: (message: string) => void;
  handleErrorWithLogout: (error: any) => void;
}

export const useFlowLocations = (selectedFlow: number | null, { showSuccessMessage, handleErrorWithLogout }: ErrorHandlerProps) => {
  const [flowLocations, setFlowLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const [locationConfig, setLocationConfig] = useState<LocationConfig>({ name: '' });
  const [openModal, setOpenModal] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [isMoving, setIsMoving] = useState(false);

  const fetchFlowLocations = async () => {
    if (selectedFlow) {
      try {
        const response = await dataProvider.getListFlowLocation({
          flowId: selectedFlow,
        });
        const locations = response.data;
        const sortedLocations = locations.sort((a: any, b: any) => a.orderIndex - b.orderIndex);
        setFlowLocations(sortedLocations);
        if (sortedLocations.length > 0 && selectedLocation === null) {
          setSelectedLocation(0); 
        }
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    } else {
      setFlowLocations([]);
      setSelectedLocation(null);
    }
  };

  const handleAddLocation = async () => {
    if (selectedFlow && newLocationName.trim()) {
      try {
        await dataProvider.addFlowLocation( {
          flowId: selectedFlow,
          locationName: newLocationName,
        });
        setNewLocationName('');
        setOpenModal(false);
        const response = await dataProvider.getListFlowLocation( { flowId: selectedFlow });
        setFlowLocations(response.data);
        if (response.data.length > 0) {
          setSelectedLocation(response.data.length - 1); 
        }
        showSuccessMessage('Location added successfully.');
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    }
  };

  const handleRemoveLocation = async () => {
    if (selectedFlow && selectedLocation !== null) {
      try {
        await dataProvider.removeFlowLocation({
          flowId: selectedFlow,
          locationIndex: selectedLocation, 
        });
        
        const response = await dataProvider.getListFlowLocation({ flowId: selectedFlow });
        setFlowLocations(response.data);
        if (response.data.length > 0) {
          setSelectedLocation(0); 
        } else {
          setSelectedLocation(null);
          setLocationConfig({ name: '' });
        }
        showSuccessMessage('Location removed successfully.');
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    }
  };

  const handleMoveLocation = async (direction: 'up' | 'down') => {
    if (!selectedFlow || selectedLocation === null || isMoving || flowLocations.length === 0) return;
  
    const sortedLocations = [...flowLocations].sort((a, b) => a.orderIndex - b.orderIndex);
    const maxOrderIndex = sortedLocations[sortedLocations.length - 1].orderIndex;
  
    const targetArrayIndex = direction === 'up' ? selectedLocation - 1 : selectedLocation;
    const targetLocationOrderIndex =
      direction === 'up'
        ? sortedLocations[targetArrayIndex]?.orderIndex
        : sortedLocations[targetArrayIndex + 1]?.orderIndex;
  
    if (targetArrayIndex < 0 || targetArrayIndex >= sortedLocations.length || targetLocationOrderIndex === undefined) {
      return;
    }
  
    setIsMoving(true);
    try {
      const changeOrder = (locationIndex: number, newOrderIndex: number) =>
        dataProvider.changeLocationOrder({
          flowId: selectedFlow,
          locationIndex,
          newOrderIndex,
        });
  
      await changeOrder(selectedLocation, maxOrderIndex + 1);
      await changeOrder(targetArrayIndex, sortedLocations[selectedLocation].orderIndex);
      await changeOrder(sortedLocations.length - 1, targetLocationOrderIndex);
  
      const response = await dataProvider.getListFlowLocation({ flowId: selectedFlow });
      setFlowLocations(response.data);
  
      setSelectedLocation(direction === 'up' ? targetArrayIndex : targetArrayIndex + 1);
  
      showSuccessMessage(`Location moved to ${direction} successfully.`);
    } catch (error: any) {
      handleErrorWithLogout(error);
    } finally {
      setIsMoving(false);
    }
  };

  useEffect(() => {
    fetchFlowLocations();
  }, [selectedFlow, dataProvider]);

  useEffect(() => {
    if (selectedLocation !== null && flowLocations.length > 0) {
      const sortedLocations = [...flowLocations].sort((a, b) => a.orderIndex - b.orderIndex);
      const selectedLoc = sortedLocations[selectedLocation]; 
      if (selectedLoc) {
        setLocationConfig({
          name: selectedLoc.name,
        });
      } else {
        setLocationConfig({ name: '' });
      }
    } else {
      setLocationConfig({ name: '' });
    }
  }, [selectedLocation, flowLocations]);

  return {
    flowLocations,
    selectedLocation,
    setSelectedLocation,
    locationConfig,
    setLocationConfig,
    openModal,
    setOpenModal,
    newLocationName,
    setNewLocationName,
    isMoving,
    handleAddLocation,
    handleRemoveLocation,
    handleMoveLocation,
  };
};